import { TaskStatusType } from '@riskbase/types';
import { appApiRoutes } from '@riskbase/types/http/appApi';
import type { RouteResponse } from 'types/RequestRoute';

// We could get this from @riskbase/types
export const TASK_COMMENT_ATTACHMENT_MAX_COUNT = 10;

// We could get this from @riskbase/types
export const TASK_COMMENT_ATTACHMENT_MAX_SIZE_BYTES = 500 * 1024 * 1024;

export const GENERIC_KEY_TASK_ASSIGNED_ADDITIONAL_DETAILS_EXPANDED =
  'GENERIC_KEY_TASK_ASSIGNED_ADDITIONAL_DETAILS_EXPANDED';

export const GENERIC_KEY_TASK_ASSIGNED_COMMENTS_EXPANDED =
  'GENERIC_KEY_TASK_ASSIGNED_COMMENTS_EXPANDED';

export const GENERIC_KEY_TASK_ASSIGNED_PHOTOS_EXPANDED =
  'GENERIC_KEY_TASK_ASSIGNED_PHOTOS_EXPANDED';

export type TaskAssignedSectionExpansionKey =
  | typeof GENERIC_KEY_TASK_ASSIGNED_ADDITIONAL_DETAILS_EXPANDED
  | typeof GENERIC_KEY_TASK_ASSIGNED_COMMENTS_EXPANDED
  | typeof GENERIC_KEY_TASK_ASSIGNED_PHOTOS_EXPANDED;

export type TasksAssignedFilter = {
  organisations: string[];
  properties: string[];
  search: string;
  dueDateFrom: string;
  dueDateTo: string;
  statuses: string[];
};

export type TasksAssignedFilterOptions = RouteResponse<
  typeof appApiRoutes.tasks.getFilterOptions
>;

const TasksAssignedNonDefaultStatusIds = ['completed', 'cancelled'];

export const getTasksAssignedDefaultStatusIds = (
  statuses: TasksAssignedFilterOptions['statuses'],
): string[] =>
  statuses
    .filter((status) => !TasksAssignedNonDefaultStatusIds.includes(status.id))
    .map((status) => status.id);

export const areTasksAssignedStatusIdsDefault = (
  statusIds: string[],
  availableStatuses: TasksAssignedFilterOptions['statuses'],
): boolean =>
  statusIds.length ===
    getTasksAssignedDefaultStatusIds(availableStatuses).length &&
  getTasksAssignedDefaultStatusIds(availableStatuses).every((statusId) =>
    statusIds.includes(statusId),
  );

export const TasksAssignedDefaultFilter: TasksAssignedFilter = {
  organisations: [],
  properties: [],
  search: '',
  dueDateFrom: '',
  dueDateTo: '',
  statuses: [],
};

export const TasksAssignedDefaultFilterOptions: TasksAssignedFilterOptions = {
  organisationRequired: false,
  organisations: [],
  properties: [],
  statuses: [],
};

export type TasksListResponse = RouteResponse<typeof appApiRoutes.tasks.get>;
export type TaskListItem = TasksListResponse['data'][number];
export type TaskDueGroup = TasksListResponse['included']['dueGroups'][number];
export type TaskPagination = TasksListResponse['pagination'];

export type TaskDetailsResponse = RouteResponse<
  typeof appApiRoutes.tasks.getDetails
>;

export type TaskDetailsState = {
  isLoading: boolean;
  id: string | null;
  item: TaskDetailsResponse | null;
  error: string | null;
};

export type TaskPaginationState = {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  statusTotals: {
    statusType: TaskStatusType | null;
    total: number;
  }[];
};

export type TasksAssignedStore = {
  items: TaskListItem[];
  dueGroups: TaskDueGroup[];
  selectedId: string | null;
  isLoading: boolean;
  isLoaded: boolean;
  filter: TasksAssignedFilter;
  filterOptions: TasksAssignedFilterOptions;
  filterOptionsDraft: TasksAssignedFilterOptions | null;
  filterOptionsLoadStatus: null | 'loading' | 'loaded' | 'error';
  taskPagination: TaskPaginationState;
  error?: string;
  taskDetails: TaskDetailsState;
};

export type TaskStore = {
  tasksAssigned: TasksAssignedStore;
};
